import React, { useState, useEffect } from 'react';
import PageApp from '../../../Page';
import {
  Table,
  Button,
  Form,
  Input,
  Modal,
  Divider,
  Space,
  message,
  Popconfirm,
  Empty,
  Spin,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  SearchOutlined,
  SettingOutlined,
  ClockCircleFilled,
  TeamOutlined,
  HomeOutlined,
} from '@ant-design/icons';
import { getList, createManual, updateManual, deleteManual } from '../../../../apis/OdooCuentaApi';
import styled from 'styled-components';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px;
`;

const routes = [  {
    href: '',
    title: <>
      <HomeOutlined />
      <span>Inicio</span>
    </>,
  },
  {
    href: '',
    title: (
      <>
        <SettingOutlined />
        <span>Configuración</span>
      </>
    ),
  },
  {
    href: '',
    title: (
      <>
        <TeamOutlined />
        <span>Recursos Humanos</span>
      </>
    ),
  },
  {
    href: '',
    title: (
      <>
        <ClockCircleFilled />
        <span>Gestión de Cuentas Contables</span>
      </>
    ),
}];

const OdooCuentaView = () => {
  const [form] = Form.useForm();
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [searchText, setSearchText] = useState('');

  // Load accounts on component mount
  useEffect(() => {
    cargarCuentas();
  }, []);

  const cargarCuentas = async () => {
    try {
      setLoading(true);
      const response = await getList();
      setCuentas(response.payload || []);
    } catch (error) {
      message.error('Error al cargar las cuentas: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (cuenta = null) => {
    if (cuenta) {
      setEditingId(cuenta.id);
      form.setFieldsValue({
        code: cuenta.code,
        name: cuenta.name,
      });
    } else {
      setEditingId(null);
      form.resetFields();
    }
    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setEditingId(null);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    try {
      setSubmitting(true);
      
      if (editingId) {
        // Update existing account
        await updateManual(editingId, values);
        message.success('Cuenta actualizada correctamente');
      } else {
        // Create new account
        await createManual(values);
        message.success('Cuenta creada correctamente');
      }
      
      handleCloseModal();
      await cargarCuentas();
    } catch (error) {
      message.error(error.message || 'Error al guardar la cuenta');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id, code) => {
    try {
      await deleteManual(id);
      message.success(`Cuenta ${code} eliminada correctamente`);
      await cargarCuentas();
    } catch (error) {
      message.error('Error al eliminar la cuenta: ' + error.message);
    }
  };

  const cuentasFiltradas = cuentas.filter((item) => {
    const term = (searchText || '').trim().toLowerCase();
    if (!term) return true;

    const code = (item.code || '').toLowerCase();
    const description = (item.display_name || '').toLowerCase();
    const group = (item.group_name || '').toLowerCase();

    return code.includes(term) || description.includes(term) || group.includes(term);
  });

  const columns = [
    {
      title: 'Código',
      dataIndex: 'code',
      key: 'code',
      width: 150,
      sorter: (a, b) => a.code.localeCompare(b.code),
    },
    {
      title: 'Descripción',
      dataIndex: 'display_name',
      key: 'display_name',
      width: 350,
    },
    {
      title: 'Grupo',
      dataIndex: 'group_name',
      key: 'group_name',
      width: 120,
    },
    {
      title: 'Activo',
      dataIndex: 'active',
      key: 'active',
      width: 80,
      render: (active) => (active ? 'Sí' : 'No'),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleOpenModal(record)}
          />
          <Popconfirm
            title="Eliminar Cuenta"
            description={`¿Está seguro de que desea eliminar la cuenta ${record.code}?`}
            onConfirm={() => handleDelete(record.id, record.code)}
            okText="Sí"
            cancelText="No"
            icon={<ExclamationCircleOutlined />}
          >
            <Button type="primary" danger size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return <PageApp
    routes={routes}
    title="Gestión de Cuentas Contables"
    cardTitle="Gestión de Cuentas Contables"
    extraCard={[
      <Space key="controls">
        <Input
          allowClear
          style={{ width: 300 }}
          prefix={<SearchOutlined />}
          placeholder="Buscar por código, descripción o grupo"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => handleOpenModal()}
        >
          Nueva Cuenta
        </Button>
      </Space>
    ]}
  >
        <Spin spinning={loading} tip="Cargando cuentas...">
          {cuentasFiltradas.length > 0 ? (
            <>
              <Table
                columns={columns}
                dataSource={cuentasFiltradas}
                rowKey="id"
                pagination={{
                  pageSize: 50,
                  showSizeChanger: true,
                  showTotal: (total) => `Total: ${total} cuentas`,
                }}
                scroll={{ x: 'max-content' }}
              />
              <Divider />
              <p style={{ color: '#666', fontSize: '12px' }}>
                Total de cuentas: {cuentasFiltradas.length}
              </p>
            </>
          ) : (
            <Empty
              description={searchText ? 'Sin coincidencias para la búsqueda' : 'No hay cuentas registradas'}
              style={{ marginTop: '40px' }}
            />
          )}
        </Spin>

      {/* Modal for Create/Edit */}
      <Modal
        title={editingId ? 'Editar Cuenta' : 'Nueva Cuenta'}
        visible={modalVisible}
        onOk={() => form.submit()}
        onCancel={handleCloseModal}
        confirmLoading={submitting}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="code"
            label="Código de Cuenta"
            rules={[
              {
                required: true,
                message: 'Por favor ingrese el código de la cuenta',
              },
              {
                pattern: /^[\d.]+$/,
                message: 'El código debe contener solo números y puntos',
              },
            ]}
          >
            <Input
              placeholder="Ej: 105.200.09"
              disabled={editingId ? false : false}
            />
          </Form.Item>

          <Form.Item
            name="name"
            label="Nombre de Cuenta"
            rules={[
              {
                required: true,
                message: 'Por favor ingrese el nombre de la cuenta',
              },
            ]}
          >
            <Input placeholder="Ej: Bancos Nacionales" />
          </Form.Item>

          {editingId}
        </Form>
      </Modal>
  </PageApp>
};

export default OdooCuentaView;
